// ArticlePage.jsx - Individual blog article page with SEO
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import DOMPurify from 'dompurify';
import {
  Calendar, Clock, ArrowLeft, Share2, Twitter, Linkedin,
  Facebook, ChevronRight, Tag, User, BookOpen
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '';

export default function ArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchArticle();
  }, [slug]);

  const fetchArticle = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/articles/public/${slug}`);
      const data = await response.json();

      if (data.ok) {
        setArticle(data.article);
      } else {
        setError('Article not found');
      }
    } catch (error) {
      console.error('Error fetching article:', error);
      setError('Failed to load article');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const estimateReadTime = (content) => {
    const wordsPerMinute = 200;
    const text = content?.replace(/<[^>]*>/g, '') || '';
    const words = text.split(/\s+/).length;
    const minutes = Math.ceil(words / wordsPerMinute);
    return `${minutes} min read`;
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(article?.title || '')}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Article Not Found</h1>
          <p className="text-gray-600 mb-8">The article you're looking for doesn't exist or has been removed.</p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  // Generate JSON-LD structured data
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.meta_description || article.excerpt,
    image: article.featured_image,
    datePublished: article.published_at,
    dateModified: article.updated_at || article.published_at,
    author: {
      '@type': 'Person',
      name: article.author_name || 'Pro IRP Team'
    },
    publisher: {
      '@type': 'Organization',
      name: 'Pro IRP',
      logo: {
        '@type': 'ImageObject',
        url: 'https://proirp.com/logo.png'
      }
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://proirp.com/blog/${article.slug}`
    }
  };

  // FAQ Schema if FAQs exist
  const faqJsonLd = article.faqs && article.faqs.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: article.faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  } : null;

  return (
    <>
      <Helmet>
        <title>{article.meta_title || article.title} | Pro IRP</title>
        <meta name="description" content={article.meta_description || article.excerpt} />
        {article.keywords && article.keywords.length > 0 && (
          <meta name="keywords" content={article.keywords.join(', ')} />
        )}
        <link rel="canonical" href={article.canonical_url || `https://proirp.com/blog/${article.slug}`} />

        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={article.meta_title || article.title} />
        <meta property="og:description" content={article.meta_description || article.excerpt} />
        <meta property="og:url" content={`https://proirp.com/blog/${article.slug}`} />
        {article.featured_image && <meta property="og:image" content={article.featured_image} />}
        <meta property="article:published_time" content={article.published_at} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.meta_title || article.title} />
        <meta name="twitter:description" content={article.meta_description || article.excerpt} />
        {article.featured_image && <meta name="twitter:image" content={article.featured_image} />}

        {/* JSON-LD */}
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        {faqJsonLd && <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>}
      </Helmet>

      <article className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero Section - Enhanced with brand colors */}
        <header className={`
          ${article.hero_style === 'dark'
            ? 'bg-gradient-to-br from-[#172A3A] to-slate-800 text-white'
            : article.hero_style === 'gradient'
            ? 'bg-gradient-to-br from-[#172A3A] via-[#1e3a50] to-[#2563eb] text-white'
            : 'bg-gradient-to-br from-[#172A3A] to-[#1e3a50] text-white'
          }
        `}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm mb-6 text-blue-200/80">
              <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
              <ChevronRight className="w-4 h-4" />
              {article.category && (
                <>
                  <Link to={`/blog?category=${article.category}`} className="hover:text-white transition-colors capitalize">
                    {article.category.replace(/-/g, ' ')}
                  </Link>
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
              <span className="truncate text-white/60">{article.title}</span>
            </nav>

            {/* Category Tag - Gold accent */}
            {article.category && (
              <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold mb-6 bg-[#FFB800] text-[#172A3A] shadow-lg">
                <Tag className="w-3 h-3" />
                {article.category.replace(/-/g, ' ')}
              </span>
            )}

            {/* Title */}
            <h1 className="text-3xl lg:text-4xl xl:text-5xl font-bold mb-6 text-white leading-tight">
              {article.title}
            </h1>

            {/* Subtitle */}
            {article.subtitle && (
              <p className="text-xl mb-8 text-blue-100/90 leading-relaxed max-w-3xl">
                {article.subtitle}
              </p>
            )}

            {/* Meta - Enhanced styling */}
            <div className="flex flex-wrap items-center gap-6 text-sm text-blue-200/80">
              {/* Author */}
              <div className="flex items-center gap-3">
                {article.author_image ? (
                  <img
                    src={article.author_image}
                    alt={article.author_name}
                    className="w-10 h-10 rounded-full border-2 border-[#FFB800]/50"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-[#FFB800] flex items-center justify-center">
                    <span className="text-[#172A3A] font-bold text-sm">
                      {(article.author_name || 'P')[0]}
                    </span>
                  </div>
                )}
                <div>
                  <span className="text-white font-medium">{article.author_name || 'Pro IRP Team'}</span>
                  {article.author_title && (
                    <span className="block text-xs text-blue-200/60">{article.author_title}</span>
                  )}
                </div>
              </div>

              <div className="hidden sm:block w-px h-6 bg-blue-400/30" />

              {/* Date */}
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#FFB800]" />
                {formatDate(article.published_at)}
              </span>

              <div className="hidden sm:block w-px h-6 bg-blue-400/30" />

              {/* Read Time */}
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#FFB800]" />
                {estimateReadTime(article.content)}
              </span>
            </div>
          </div>
        </header>

        {/* Featured Image */}
        {article.featured_image && (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-4 mb-8">
            <img
              src={article.featured_image}
              alt={article.featured_image_alt || article.title}
              className="w-full rounded-xl shadow-lg"
            />
          </div>
        )}

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 lg:p-14 relative overflow-hidden">
            {/* Subtle accent line */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#172A3A] via-[#FFB800] to-[#172A3A]" />

            {/* Article Content */}
            <div
              className="prose prose-lg max-w-none
                prose-headings:font-bold prose-headings:text-[#172A3A]
                prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-h2:pb-2 prose-h2:border-b prose-h2:border-gray-100
                prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-h3:text-[#1e3a50]
                prose-p:text-gray-700 prose-p:leading-relaxed
                prose-a:text-[#2563eb] prose-a:no-underline hover:prose-a:underline prose-a:font-medium
                prose-ul:text-gray-700 prose-ol:text-gray-700
                prose-li:marker:text-[#FFB800]
                prose-strong:text-[#172A3A] prose-strong:font-semibold
                prose-blockquote:border-l-4 prose-blockquote:border-l-[#FFB800] prose-blockquote:bg-amber-50 prose-blockquote:py-4 prose-blockquote:px-6 prose-blockquote:rounded-r-xl prose-blockquote:not-italic prose-blockquote:text-gray-700 prose-blockquote:my-8
              "
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content) }}
            />

            {/* FAQs Section - Enhanced */}
            {article.faqs && article.faqs.length > 0 && (
              <div className="mt-16 pt-10 border-t-2 border-gray-100">
                <h2 className="text-2xl font-bold text-[#172A3A] mb-8 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-[#FFB800] flex items-center justify-center">
                    <span className="text-[#172A3A] font-bold text-lg">?</span>
                  </span>
                  Frequently Asked Questions
                </h2>
                <div className="space-y-4">
                  {article.faqs.map((faq, index) => (
                    <div key={index} className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border border-gray-100 hover:border-[#FFB800]/50 hover:shadow-md transition-all">
                      <h3 className="font-semibold text-[#172A3A] mb-3 text-lg">
                        {faq.question}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sources */}
            {article.sources && article.sources.length > 0 && (
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <BookOpen className="w-5 h-5" />
                  Sources
                </h3>
                <ul className="space-y-2">
                  {article.sources.map((source, index) => (
                    <li key={index} className="text-sm text-gray-600">
                      <a
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {source.title}
                      </a>
                      {source.publisher && ` — ${source.publisher}`}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Share */}
            <div className="mt-8 pt-8 border-t border-gray-200">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <span className="text-gray-700 font-medium flex items-center gap-2">
                  <Share2 className="w-5 h-5" />
                  Share this article
                </span>
                <div className="flex items-center gap-3">
                  <a
                    href={shareLinks.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-gray-100 rounded-full hover:bg-blue-100 hover:text-blue-600 transition-colors"
                    aria-label="Share on Twitter"
                  >
                    <Twitter className="w-5 h-5" />
                  </a>
                  <a
                    href={shareLinks.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-gray-100 rounded-full hover:bg-blue-100 hover:text-blue-600 transition-colors"
                    aria-label="Share on LinkedIn"
                  >
                    <Linkedin className="w-5 h-5" />
                  </a>
                  <a
                    href={shareLinks.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-gray-100 rounded-full hover:bg-blue-100 hover:text-blue-600 transition-colors"
                    aria-label="Share on Facebook"
                  >
                    <Facebook className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section - Enhanced with brand colors */}
          <div className="mt-12 bg-gradient-to-br from-[#172A3A] via-[#1e3a50] to-[#172A3A] rounded-2xl p-10 lg:p-14 text-white text-center relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#FFB800]/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl" />

            <div className="relative">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FFB800]/20 rounded-full text-[#FFB800] text-sm font-medium mb-6">
                <span className="w-2 h-2 bg-[#FFB800] rounded-full animate-pulse" />
                Join 500+ Medicare agents
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                {article.cta_title || 'Ready to Improve Your Retention?'}
              </h2>
              <p className="text-blue-100/80 mb-8 max-w-2xl mx-auto text-lg leading-relaxed">
                {article.cta_description || 'Pro IRP helps Medicare agents systematically protect and grow their book of business with proactive retention tools.'}
              </p>
              <Link
                to={article.cta_button_url || '/signup'}
                className="inline-flex items-center gap-3 bg-[#FFB800] text-[#172A3A] px-8 py-4 rounded-xl font-bold text-lg hover:bg-[#e5a600] transition-all hover:scale-105 shadow-lg shadow-[#FFB800]/25"
              >
                {article.cta_button_text || 'Start Free Trial'}
                <ChevronRight className="w-5 h-5" />
              </Link>
              <p className="text-xs text-blue-200/50 mt-4">No credit card required • 14-day free trial</p>
            </div>
          </div>

          {/* Back to Blog */}
          <div className="mt-8 text-center">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to All Articles
            </Link>
          </div>
        </div>
      </article>
    </>
  );
}
